import { type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from '@remix-run/react';
import { ThemeModeScript, ThemeProvider } from 'flowbite-react';
import { useChangeLanguage } from 'remix-i18next/react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { Toaster } from 'sonner';
import { getLocale, getMetaTitle } from '#buyer-portal/i18n/i18n.server';
import { getUser } from '#buyer-portal/session.server';
import { GenericErrorBoundary } from '#common/components/ErrorBoundary.tsx';
import { StepUpProvider } from '#common/features/auth/StepUp/context.tsx';
import { validateEditMode } from '#common/i18n/edit-mode.server.ts';
import { EditModeProvider } from '#common/i18n/edit-mode.tsx';
import { localeCookie } from '#common/i18n/i18n.server';
import { serializeCookieByConsent } from '#common/utils/consent.server.js';
import { honeypot } from '#common/utils/honeypot.server';
import { DefaultLangProvider, getUrlWithoutLang } from '#common/utils/i18n.js';
import '#buyer-portal/styles/app.css';
import { defaultLang } from './i18n/config';
import { crispyvoiceTheme } from '#common/theme.ts';
import { environment, PublicEnv } from './utils/public-env';
import { ConsentHandling } from '#common/utils/consent-handling.tsx';

export const handle = { i18n: [ 'common', 'footer', 'navigation', 'contact', 'auth' ] };

export async function loader(args: LoaderFunctionArgs) {
    const { request, params } = args;
    const user = await getUser(request);
    const locale = getLocale(user, params);
    const honeyProps = await honeypot.getInputProps();
    const env = environment();

    return typedjson({
        locale,
        title: await getMetaTitle(request),
        honeyProps,
        user,
        env,
        baseUrl: getUrlWithoutLang(request.url),
        isEditModeValid: validateEditMode(request)
    }, { headers: { 'Set-Cookie': await serializeCookieByConsent(request, 'functionality', localeCookie, locale) } });
}

export default function App() {
    const { locale, honeyProps, env } = useTypedLoaderData<typeof loader>();
    useChangeLanguage(locale);
    return (
        <EditModeProvider routeId='root'>
            <HoneypotProvider {...honeyProps}>
                <ThemeProvider theme={crispyvoiceTheme}>
                    <DefaultLangProvider value={defaultLang}>
                        <html
                            lang={locale ?? 'en'}
                            className='h-full scroll-smooth bg-slate-50 antialiased'
                            translate='no'
                            suppressHydrationWarning
                        >
                            <head>
                                <meta charSet='utf-8' />
                                <meta name='viewport' content='width=device-width, initial-scale=1' />
                                <ConsentHandling />
                                <Meta />
                                <Links />
                                <ThemeModeScript />
                                <PublicEnv {...env} />
                            </head>
                            <body className='h-full w-full scroll-smooth border-l-4 border-crispyblue-700 dark:border-crispyblue-700'>
                                <StepUpProvider>
                                    <main className='h-full w-full relative z-0'>
                                        <Outlet />
                                    </main>
                                    <Toaster richColors position='top-center' />
                                    <ScrollRestoration />
                                    <Scripts />
                                </StepUpProvider>
                            </body>
                        </html>
                    </DefaultLangProvider>
                </ThemeProvider>
            </HoneypotProvider>
        </EditModeProvider>
    );
}

export const links: LinksFunction = () => {
    return [ { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/apple-touch-icon.png' }, {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicons/favicon-32x32.png'
    }, { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicons/favicon-16x16.png' } ];
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    return [ { title: data?.title ?? 'Error' } ];
};

export function ErrorBoundary() {
    return (
        <html lang='en' className='h-full scroll-smooth bg-slate-50 antialiased'>
            <head>
                <meta charSet='utf-8' />
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <Meta />
                <Links />
            </head>
            <body className='h-full w-full'>
                <GenericErrorBoundary homePath='/' />
                <Scripts />
            </body>
        </html>
    );
}
